import React, { useMemo } from 'react';
import { Modal, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import MaskedInput from 'react-text-mask';
import moment from 'moment';
import { toast } from 'react-toastify';
import { FaUndo, FaTimes } from 'react-icons/fa';

import { selectStylesSm, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { estornarTitulosEmLote } from '~/Services/Service';
import { extrairMensagemErro } from '~/Utils/Helpers';
import { MASK_DATE } from '~/constant';

const TIPOS_ESTORNO = [
  { value: 'I', label: 'Interno' },
  { value: 'B', label: 'Banco' },
];

const MAX_MOTIVO = 256;
const DATA_FORMATO_BR = 'DD/MM/YYYY';

const dataBrParaIso = dataBr => {
  if (!dataBr) return null;
  const data = moment(dataBr, DATA_FORMATO_BR, true);
  return data.isValid() ? data.format('YYYY-MM-DD') : null;
};

const estornoSchema = Yup.object().shape({
  codigoPortadorBx: Yup.number()
    .typeError('Portador BX obrigatório')
    .required('Portador BX obrigatório')
    .positive()
    .integer(),
  tipoEstorno: Yup.string()
    .required('Tipo de estorno obrigatório')
    .oneOf(['I', 'B'], 'Tipo inválido'),
  dataContabil: Yup.string()
    .required('Data contábil obrigatória')
    .test('formato', 'Formato dd/mm/aaaa', val => {
      if (!val) return false;
      return /^\d{2}\/\d{2}\/\d{4}$/.test(val);
    })
    .test('nao-futura', 'Data contábil não pode ser futura', val => {
      const data = moment(val, DATA_FORMATO_BR, true);
      if (!data.isValid()) return false;
      return !data.isAfter(moment(), 'day');
    }),
  motivo: Yup.string()
    .nullable()
    .max(MAX_MOTIVO, `Motivo deve ter até ${MAX_MOTIVO} caracteres`),
});

const ModalConfirmarEstorno = ({
  aberto,
  titulosSelecionados,
  onFechar,
  onSucesso,
  enviando,
  setEnviando,
}) => {
  // RN-3 — sugere Portador BX do primeiro título; alertas se divergir entre o lote
  const portadorPredominante = useMemo(() => {
    if (!titulosSelecionados || titulosSelecionados.length === 0) return null;
    return titulosSelecionados[0].conta_portador_bx || null;
  }, [titulosSelecionados]);

  const portadoresDivergentes = useMemo(() => {
    if (!titulosSelecionados || titulosSelecionados.length === 0) return false;
    const primeiro = titulosSelecionados[0].conta_portador_bx;
    return titulosSelecionados.some(t => t.conta_portador_bx !== primeiro);
  }, [titulosSelecionados]);

  const codigoAtivoPredominante = useMemo(() => {
    if (!titulosSelecionados || titulosSelecionados.length === 0) return null;
    return titulosSelecionados[0].codigo_ativo || null;
  }, [titulosSelecionados]);

  const codigoContaCedente = useMemo(() => {
    if (!titulosSelecionados || titulosSelecionados.length === 0) return null;
    return titulosSelecionados[0].codigo_cedente || null;
  }, [titulosSelecionados]);

  const opcoesPortadorBx = useMemo(() => {
    const mapa = new Map();
    titulosSelecionados.forEach(t => {
      if (t.conta_portador_bx && !mapa.has(t.conta_portador_bx)) {
        mapa.set(t.conta_portador_bx, {
          value: t.conta_portador_bx,
          label: `${t.conta_portador_bx} - ${t.apelido_portador_bx ||
            'Sem apelido'}`,
        });
      }
    });
    return Array.from(mapa.values());
  }, [titulosSelecionados]);

  const valoresIniciais = {
    codigoPortadorBx: portadorPredominante || '',
    tipoEstorno: 'I',
    dataContabil: moment().format('DD/MM/YYYY'),
    motivo: '',
  };

  const submeter = async valores => {
    if (!titulosSelecionados || titulosSelecionados.length === 0) {
      toast.error('Nenhum título selecionado para estorno.');
      return;
    }
    const dataContabilIso = dataBrParaIso(valores.dataContabil);
    if (!dataContabilIso) {
      toast.error('Data contábil inválida.');
      return;
    }
    if (!codigoContaCedente) {
      toast.error('Conta cedente é obrigatória.');
      return;
    }
    const payload = {
      codigos_titulo: titulosSelecionados.map(t => t.codigo_titulo),
      codigo_portador_bx: Number(valores.codigoPortadorBx),
      codigo_conta_cedente: Number(codigoContaCedente),
      tipo_estorno: valores.tipoEstorno,
      data_contabil: dataContabilIso,
      motivo: valores.motivo ? valores.motivo.trim() : null,
      codigo_ativo: codigoAtivoPredominante,
    };

    try {
      setEnviando(true);
      const resposta = await estornarTitulosEmLote(payload);
      const mensagem =
        (resposta && resposta.mensagem) ||
        `${titulosSelecionados.length} título(s) estornado(s) com sucesso.`;
      toast.success(mensagem);
      await onSucesso();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setEnviando(false);
    }
  };

  if (!aberto) return null;

  return (
    <Modal
      show={aberto}
      onHide={onFechar}
      backdrop={enviando ? 'static' : true}
      keyboard={!enviando}
      size="lg"
      centered
    >
      <Modal.Header closeButton={!enviando}>
        <Modal.Title>
          {`Confirmar estorno de ${titulosSelecionados.length} título(s)`}
        </Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={valoresIniciais}
        validationSchema={estornoSchema}
        enableReinitialize
        onSubmit={submeter}
      >
        {({
          handleSubmit,
          setFieldValue,
          values,
          errors,
          touched,
          isValid,
        }) => (
          <Form onSubmit={handleSubmit} noValidate>
            <Modal.Body>
              {portadoresDivergentes && (
                <div className="alert alert-warning small mb-3">
                  Os títulos selecionados têm Portador BX diferentes. Todos do
                  lote precisam ter o mesmo Portador BX para o estorno ser
                  executado.
                </div>
              )}
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="codigoPortadorBx">
                    <Form.Label className="small mb-1">
                      Portador BX <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      placeholder="Selecione o Portador BX..."
                      noOptionsMessage={() => 'Nenhum encontrado'}
                      styles={selectStylesSm}
                      components={{ DropdownIndicator }}
                      options={opcoesPortadorBx}
                      value={
                        opcoesPortadorBx.find(
                          o => o.value === values.codigoPortadorBx
                        ) || null
                      }
                      onChange={opcao =>
                        setFieldValue(
                          'codigoPortadorBx',
                          opcao ? opcao.value : ''
                        )
                      }
                      isDisabled={enviando}
                      isClearable
                    />
                    {touched.codigoPortadorBx && errors.codigoPortadorBx && (
                      <div className="text-danger small mt-1">
                        {errors.codigoPortadorBx}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Group>
                    <Form.Label className="small mb-1 d-block">
                      Tipo de estorno <span className="text-danger">*</span>
                    </Form.Label>
                    {TIPOS_ESTORNO.map(opcao => (
                      <Form.Check
                        key={opcao.value}
                        type="radio"
                        id={`tipo-estorno-${opcao.value}`}
                        name="tipoEstorno"
                        value={opcao.value}
                        label={opcao.label}
                        checked={values.tipoEstorno === opcao.value}
                        onChange={() =>
                          setFieldValue('tipoEstorno', opcao.value)
                        }
                        disabled={enviando}
                        inline
                      />
                    ))}
                    {touched.tipoEstorno && errors.tipoEstorno && (
                      <div className="text-danger small mt-1">
                        {errors.tipoEstorno}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Group controlId="dataContabil">
                    <Form.Label className="small mb-1">
                      Data contábil <span className="text-danger">*</span>
                    </Form.Label>
                    <MaskedInput
                      mask={MASK_DATE}
                      guide={false}
                      name="dataContabil"
                      type="tel"
                      placeholder="dd/mm/aaaa"
                      value={values.dataContabil}
                      onChange={e =>
                        setFieldValue('dataContabil', e.target.value)
                      }
                      disabled={enviando}
                      className={`form-control form-control-sm ${
                        touched.dataContabil && errors.dataContabil
                          ? 'is-invalid'
                          : ''
                      }`}
                    />
                    {touched.dataContabil && errors.dataContabil && (
                      <div className="text-danger small mt-1">
                        {errors.dataContabil}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Form.Group controlId="motivo">
                    <Form.Label className="small mb-1">
                      {`Motivo (opcional, máx ${MAX_MOTIVO} caracteres)`}
                    </Form.Label>
                    <Field
                      as="textarea"
                      name="motivo"
                      rows={3}
                      maxLength={MAX_MOTIVO}
                      className={`form-control form-control-sm ${
                        touched.motivo && errors.motivo ? 'is-invalid' : ''
                      }`}
                      disabled={enviando}
                    />
                    {touched.motivo && errors.motivo && (
                      <div className="text-danger small mt-1">
                        {errors.motivo}
                      </div>
                    )}
                    <small className="text-muted">
                      {`${(values.motivo || '').length}/${MAX_MOTIVO}`}
                    </small>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={onFechar}
                disabled={enviando}
              >
                <FaTimes className="mr-1" />
                {' Cancelar'}
              </Button>
              <Button
                variant="warning"
                size="sm"
                type="submit"
                disabled={enviando || !isValid}
              >
                {enviando ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="mr-2"
                    />
                    {' Estornando...'}
                  </>
                ) : (
                  <>
                    <FaUndo className="mr-1" />
                    {` Confirmar estorno`}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ModalConfirmarEstorno;
